<template>
  <Button
    severity="secondary"
    aria-label="Organization member menu"
    aria-haspopup="true"
    aria-controls="organization_member_menu"
    class="m-0 flex w-full items-center justify-between px-2 py-1"
    @click="toggleOrganizationMemberMenu"
  >
    <div
      v-if="isFetchingMember"
      class="flex w-full items-center"
    >
      <Skeleton
        shape="circle"
        size="42px"
        class="mr-2 shrink-0"
      />
      <Skeleton width="100%" />
    </div>

    <template v-else>
      <Avatar
        :image="member.avatar?.photoURL"
        size="large"
        shape="circle"
        class="shrink-0"
      />
      <span class="ml-1 mr-2 w-full truncate text-left">
        {{ member.displayName }}
      </span>
    </template>
  </Button>

  <Menu
    id="organization_member_menu"
    ref="organizationMemberMenu"
    :model="organizationMemberMenuItems"
    :popup="true"
    class="max-w-[218px]"
  >
    <template #start>
      <div class="flex flex-col px-4 py-2 text-left">
        <span
          :title="member.displayName"
          class="truncate font-bold"
        >
          {{ member.displayName }}
        </span>
        <span class="truncate text-xs">{{ member.member.email }}</span>
      </div>
    </template>

    <template #item="{ item, props }">
      <nuxt-link
        v-if="item.to"
        v-slot="{ href, navigate }"
        :to="item.to"
        custom
      >
        <a
          v-ripple
          :href="href"
          v-bind="props.action"
          @click="navigate"
        >
          <LucideIcon :icon="item.icon" />
          <span>{{ item.label }}</span>
        </a>
      </nuxt-link>
      <a
        v-else
        v-ripple
        :href="item.url"
        :target="item.target"
        v-bind="props.action"
      >
        <LucideIcon :icon="item.icon" />
        <span>{{ item.label }}</span>
      </a>
    </template>

    <template #end>
      <Button
        text
        class="w-full rounded-none"
        @click="signOutUser"
      >
        <LucideIcon :icon="LogOut" />
        <span>{{ $t("layouts.default.app-top-bar.menu-items.log-out") }}</span>
      </Button>
    </template>
  </Menu>

  <ProfileDialog
    :visible="isProfileDialogVisible"
    @hide="hideProfileDialog"
  />
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { doc } from '@firebase/firestore'
import { Lock, LogOut, UserPen } from 'lucide-vue-next'
import ProfileDialog from '../../structural/profile/ProfileDialog.vue'
import LucideIcon from '../../ui/LucideIcon.vue'
import { useLayoutStore } from '~/store/layout'
import { useOrganizationsStore } from '~/store/organizations'
import type { OrganizationMember } from '~/types/organizations/member'
import { Collections } from '~/constants/firebase'
import { AppRoutes } from '~/constants/routes'
import { AppRoles } from '~/constants/user'

const currentUser = await getCurrentUser()
const organizationsStore = useOrganizationsStore()
const { selectedOrganization } = storeToRefs(organizationsStore)
const { data: member, pending: isFetchingMember } = useDocument<OrganizationMember>(
  computed(() => doc(
    useGetFirestore(),
    Collections.Organizations.members(selectedOrganization.value?.id),
    currentUser.uid,
  )),
  {
    wait: true,
    ssrKey: `${Collections.Organizations.members(selectedOrganization.value?.id)}-${currentUser.uid}`,
  },
)

const { t } = useI18n()
const organizationMemberMenu = ref()
const layoutStore = useLayoutStore()
const organizationMemberMenuItems = computed(() => {
  const isCurrentUserAppAdmin = isCurrentUserGrantedInApp(AppRoles.Admin)
  return [
    {
      label: t('components.layouts.common.organization-member-menu.profile'),
      icon: UserPen,
      command: (event: MouseEvent) => {
        layoutStore.closeMobileMenu()
        showProfileDialog(event)
      },
    },
    ...(getFormattedAppConfigMenuItems('user').length > 0 || isCurrentUserAppAdmin
      ? [{ separator: true }]
      : []
    ),
    ...getFormattedAppConfigMenuItems('user'),
    ...(isCurrentUserAppAdmin
      ? [
          {
            label: t('components.layouts.common.organization-member-menu.admin'),
            icon: Lock,
            to: getFormattedAppConfigMenuItems('admin')?.[0]?.items?.[0]?.to ?? AppRoutes.Admin.Users,
          },
        ]
      : []),
    { separator: true },
  ]
})

const toggleOrganizationMemberMenu = (event) => {
  organizationMemberMenu.value.toggle(event)
}

const isProfileDialogVisible = ref(false)
const showProfileDialog = (event: any) => {
  isProfileDialogVisible.value = true
  toggleOrganizationMemberMenu(event)
}
const hideProfileDialog = () => {
  isProfileDialogVisible.value = false
}
</script>
